import React, { useState, useEffect, useRef } from 'react';
import { TabView } from './TabView';
import { FlightManager } from './FlightManager';
import { AccommodationManager } from './AccommodationManager';
import { TourManager } from './TourManager';
import { TransportationManager } from './TransportationManager';
import { RestaurantManager } from './RestaurantManager';
import { QuotationPage as QuotationPageType, TourInfo, AccommodationInfo, QuotationBlock, RestaurantInfo } from './types';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { DragDropContext, Droppable } from '@hello-pangea/dnd';
import { QuotationBlock as QuotationBlockComponent } from './QuotationBlock';
import { cn } from '@/lib/utils';
import { Plus, BookOpen, Bookmark, Box, Plane, Building, Map, Car, Utensils } from 'lucide-react';
import { useLibrary } from '@/hooks/useLibrary';
import { LibraryBrowser } from './LibraryBrowser';
import { SaveToLibraryModal } from './SaveToLibraryModal';
import { useToast } from '@/hooks/use-toast';

interface QuotationPageTabsProps {
  page: QuotationPageType;
  updatePage: (id: string, field: keyof QuotationPageType, value: any) => void;
  updateBlock: (pageId: string, blockId: string, field: keyof QuotationBlock, value: any) => void;
  deleteBlock: (pageId: string, blockId: string) => void;
  duplicateBlock?: (pageId: string, blockId: string) => void;
  addBlock: (pageId: string) => void;
  moveTourToPage?: (sourcePageId: string, tourId: string, targetPageId: string) => void;
  moveRestaurantToPage?: (sourcePageId: string, restaurantId: string, targetPageId: string) => void;
  moveAccommodationToPage?: (sourcePageId: string, accommodationId: string, targetPageId: string) => void;
  moveFlightEventToPage?: (sourcePageId: string, flightEventId: string, targetPageId: string) => void;
  moveAccommodationEventToPage?: (sourcePageId: string, accommodationEventId: string, targetPageId: string) => void;
  moveTransportationToPage?: (sourcePageId: string, transportationId: string, targetPageId: string) => void;
  moveFlightToPage?: (sourcePageId: string, flightId: string, targetPageId: string) => void;
  moveBlockToPage?: (sourcePageId: string, blockId: string, targetPageId: string) => void;
  allPages?: QuotationPageType[]; // All pages in the quotation
}

export function QuotationPageTabs({ 
  page, 
  updatePage, 
  updateBlock, 
  deleteBlock, 
  duplicateBlock, 
  addBlock,
  moveTourToPage,
  moveRestaurantToPage,
  moveAccommodationToPage,
  moveFlightEventToPage,
  moveAccommodationEventToPage,
  moveTransportationToPage,
  moveFlightToPage,
  moveBlockToPage,
  allPages = []
}: QuotationPageTabsProps) {
  // Log para depuração
  console.log('[QuotationPageTabs] Recebendo props:', {
    pageId: page.id,
    hasMoveFlightEventToPage: Boolean(moveFlightEventToPage),
    hasMoveAccommodationEventToPage: Boolean(moveAccommodationEventToPage),
    moveFlightEventToPageType: typeof moveFlightEventToPage,
    moveAccommodationEventToPageType: typeof moveAccommodationEventToPage,
    allPagesLength: allPages?.length
  });

  // State para controlar quais blocos estão colapsados
  const [collapsedBlocks, setCollapsedBlocks] = useState<Record<string, boolean>>({});
  // Active tab
  const [activeTab, setActiveTab] = useState('blocks');
  // Library state
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);
  const [currentItemType, setCurrentItemType] = useState<string>('block');
  // Get toast
  const { toast } = useToast();
  
  // State para controlar quais acomodações estão colapsadas
  const [collapsedAccommodations, setCollapsedAccommodations] = useState<Record<string, boolean>>({});
  
  // State para controlar quais tours estão colapsados
  const [collapsedTours, setCollapsedTours] = useState<Record<string, boolean>>({});
  
  // State para controlar quais traslados estão colapsados
  const [collapsedTransportations, setCollapsedTransportations] = useState<Record<string, boolean>>({});
  
  // State para controlar quais restaurantes estão colapsados
  const [collapsedRestaurants, setCollapsedRestaurants] = useState<Record<string, boolean>>({});
  
  // State para controlar quais voos estão colapsados
  const [collapsedFlights, setCollapsedFlights] = useState<Record<string, boolean>>({});

  // Integração com a biblioteca
  const { addLibraryItemToQuotation } = useLibrary({
    onItemAdded: (item, type) => {
      // Adicionar item à página de cotação baseado no tipo
      if (type === 'block') {
        // Adicionar o bloco à página atual
        const blocks = [...page.blocks, item];
        updatePage(page.id, 'blocks', blocks);
      } else if (type === 'restaurant') {
        // Adicionar o restaurante à página atual
        const restaurants = [...page.restaurants, item];
        updatePage(page.id, 'restaurants', restaurants);
      } else if (type === 'accommodation') {
        // Adicionar a acomodação à página atual
        const accommodations = [...page.accommodations, item];
        updatePage(page.id, 'accommodations', accommodations);
      } else if (type === 'tour') {
        // Adicionar o tour à página atual
        const tours = [...page.tours, item];
        updatePage(page.id, 'tours', tours);
      } else if (type === 'transportation') {
        // Adicionar o transporte à página atual
        const transportations = [...page.transportations, item];
        updatePage(page.id, 'transportations', transportations);
      } else if (type === 'flight') {
        // Adicionar o voo à página atual
        const flights = [...page.flights, item];
        updatePage(page.id, 'flights', flights);
      }
    }
  });

  // Inicializar todos os blocos como colapsados ao montar o componente
  useEffect(() => {
    if (page.blocks?.length) {
      // Use functional update to get the current state
      setCollapsedBlocks(currentCollapsedState => {
        // Criar uma cópia do estado atual
        const updatedCollapsedState = { ...currentCollapsedState };
        
        // Garantir que todos os blocos existentes estejam no state
        page.blocks.forEach(block => {
          // Se o bloco não existe no state atual, defini-lo como colapsado (true)
          if (updatedCollapsedState[block.id] === undefined) {
            updatedCollapsedState[block.id] = true;
          }
        });
        
        return updatedCollapsedState;
      });
    } else {
      // Se não houver blocos, limpar o state
      setCollapsedBlocks({});
    }
  }, [page.id, page.blocks]);

  // Inicializar todas as acomodações como colapsadas ao montar o componente
  useEffect(() => {
    if (page.accommodations?.length) {
      setCollapsedAccommodations(currentState => {
        const updatedState = { ...currentState };
        
        page.accommodations?.forEach(accommodation => {
          if (updatedState[accommodation.id] === undefined) {
            updatedState[accommodation.id] = true;
          }
        });
        
        return updatedState;
      });
    } else {
      setCollapsedAccommodations({});
    }
  }, [page.id, page.accommodations]);

  // Inicializar todos os tours como colapsados ao montar o componente
  useEffect(() => {
    if (page.tours?.length) {
      setCollapsedTours(currentState => {
        const updatedState = { ...currentState };
        
        page.tours?.forEach(tour => {
          if (updatedState[tour.id] === undefined) {
            updatedState[tour.id] = true;
          }
        });
        
        return updatedState;
      });
    } else {
      setCollapsedTours({});
    }
  }, [page.id, page.tours]);

  // Inicializar todos os traslados como colapsados ao montar o componente
  useEffect(() => {
    if (page.transportations?.length) {
      setCollapsedTransportations(currentState => {
        const updatedState = { ...currentState };
        
        page.transportations?.forEach(transportation => {
          if (updatedState[transportation.id] === undefined) {
            updatedState[transportation.id] = true;
          }
        });
        
        return updatedState;
      });
    } else {
      setCollapsedTransportations({});
    }
  }, [page.id, page.transportations]);

  // Inicializar todos os restaurantes como colapsados ao montar o componente
  useEffect(() => {
    if (page.restaurants?.length) {
      setCollapsedRestaurants(currentState => {
        const updatedState = { ...currentState };
        
        page.restaurants?.forEach(restaurant => {
          if (updatedState[restaurant.id] === undefined) {
            updatedState[restaurant.id] = true;
          }
        });
        
        return updatedState;
      });
    } else {
      setCollapsedRestaurants({});
    }
  }, [page.id, page.restaurants]);

  // Inicializar todos os voos como colapsados ao montar o componente
  useEffect(() => {
    if (page.flights?.length) {
      setCollapsedFlights(currentState => {
        const updatedState = { ...currentState };
        
        page.flights?.forEach(flight => {
          if (updatedState[flight.id] === undefined) {
            updatedState[flight.id] = true;
          }
        });
        
        return updatedState;
      });
    } else {
      setCollapsedFlights({});
    }
  }, [page.id, page.flights]);

  // Escutar eventos de expansão de itens para colapsar os blocos quando outro item for expandido
  useEffect(() => {
    const handleItemExpanded = (event: CustomEvent) => {
      const { itemType, itemId, pageId: eventPageId } = event.detail;
      
      // Ignorar eventos de outra página
      if (eventPageId !== page.id) {
        return;
      }
      
      // Ignorar se for um evento de bloco (para evitar loops)
      if (itemType === 'block') {
        return;
      }
      
      // Colapsar todos os blocos quando outro tipo de item for expandido
      setCollapsedBlocks(currentState => {
        return Object.keys(currentState).reduce((acc, id) => {
          acc[id] = true;
          return acc;
        }, {} as Record<string, boolean>);
      });
    };
    
    // Adicionar o event listener
    document.addEventListener('item-expanded', handleItemExpanded as EventListener);
    
    // Remover ao desmontar o componente
    return () => {
      document.removeEventListener('item-expanded', handleItemExpanded as EventListener);
    };
  }, [page.id]);
  
  // Toggle para colapsar/expandir um bloco
  const toggleBlockCollapse = (blockId: string) => {
    // Use the functional state update to get the current state
    const isCurrentlyCollapsed = collapsedBlocks[blockId];
    
    if (isCurrentlyCollapsed) {
      // Disparar um evento para notificar outros componentes que um bloco foi expandido
      document.dispatchEvent(new CustomEvent('item-expanded', {
        detail: {
          itemType: 'block',
          itemId: blockId,
          pageId: page.id
        }
      }));
      
      // Then update the state to expand the block
      setTimeout(() => {
        setCollapsedBlocks(currentState => {
          // Colapsar todos os blocos primeiro
          const allCollapsed = Object.keys(currentState).reduce((acc, id) => {
            acc[id] = true;
            return acc;
          }, {} as Record<string, boolean>);
          
          // Retornar o estado com todos colapsados, exceto o atual
          return {
            ...allCollapsed,
            [blockId]: false
          };
        });
      }, 0);
    } else {
      // Se estava expandido, apenas colapsar
      setCollapsedBlocks(currentState => ({
        ...currentState,
        [blockId]: true
      }));
    }
  };

  // Toggle para colapsar/expandir uma acomodação
  const toggleAccommodationCollapse = (accommodationId: string) => {
    // If we receive an empty ID, it's just a signal to collapse all items
    if (!accommodationId) {
      setCollapsedAccommodations(currentState => {
        return Object.keys(currentState).reduce((acc, id) => {
          acc[id] = true;
          return acc;
        }, {} as Record<string, boolean>);
      });
      return;
    }
    
    const isCurrentlyCollapsed = collapsedAccommodations[accommodationId];
    
    if (isCurrentlyCollapsed) {
      // Prevent duplicate event dispatches by checking if we're already open
      // Disparar um evento para notificar outros componentes que uma acomodação foi expandida
      document.dispatchEvent(new CustomEvent('item-expanded', {
        detail: {
          itemType: 'accommodation',
          itemId: accommodationId,
          pageId: page.id
        }
      }));
      
      // Update state only once, avoid setTimeout which can cause loops
      setCollapsedAccommodations(currentState => {
        // Colapsar todas as acomodações primeiro
        const allCollapsed = Object.keys(currentState).reduce((acc, id) => {
          acc[id] = true;
          return acc;
        }, {} as Record<string, boolean>);
        
        // Retornar o estado com todas colapsadas, exceto a atual
        return {
          ...allCollapsed,
          [accommodationId]: false
        };
      });
    } else {
      // Se estava expandido, apenas colapsar
      setCollapsedAccommodations(currentState => ({
        ...currentState,
        [accommodationId]: true
      }));
    }
  };

  // Toggle para colapsar/expandir um tour
  const toggleTourCollapse = (tourId: string) => {
    // If we receive an empty ID, it's just a signal to collapse all items
    if (!tourId) {
      setCollapsedTours(currentState => {
        return Object.keys(currentState).reduce((acc, id) => {
          acc[id] = true;
          return acc;
        }, {} as Record<string, boolean>);
      });
      return;
    }
    
    const isCurrentlyCollapsed = collapsedTours[tourId];
    
    if (isCurrentlyCollapsed) {
      // Disparar um evento para notificar outros componentes que um tour foi expandido
      document.dispatchEvent(new CustomEvent('item-expanded', {
        detail: {
          itemType: 'tour',
          itemId: tourId,
          pageId: page.id
        }
      }));
      
      // Update state only once, avoid setTimeout which can cause loops
      setCollapsedTours(currentState => {
        // Colapsar todos os tours primeiro
        const allCollapsed = Object.keys(currentState).reduce((acc, id) => {
          acc[id] = true;
          return acc;
        }, {} as Record<string, boolean>);
        
        // Retornar o estado com todos colapsados, exceto o atual
        return {
          ...allCollapsed,
          [tourId]: false
        };
      });
    } else {
      // Se estava expandido, apenas colapsar
      setCollapsedTours(currentState => ({
        ...currentState,
        [tourId]: true
      }));
    }
  };

  // Toggle para colapsar/expandir um traslado
  const toggleTransportationCollapse = (transportationId: string) => {
    // If we receive an empty ID, it's just a signal to collapse all items
    if (!transportationId) {
      setCollapsedTransportations(currentState => {
        return Object.keys(currentState).reduce((acc, id) => {
          acc[id] = true;
          return acc;
        }, {} as Record<string, boolean>);
      });
      return;
    }
    
    const isCurrentlyCollapsed = collapsedTransportations[transportationId];
    
    if (isCurrentlyCollapsed) {
      // Disparar um evento para notificar outros componentes que um traslado foi expandido
      document.dispatchEvent(new CustomEvent('item-expanded', {
        detail: {
          itemType: 'transportation',
          itemId: transportationId,
          pageId: page.id
        }
      }));
      
      // Update state only once, avoid setTimeout which can cause loops
      setCollapsedTransportations(currentState => {
        // Colapsar todos os traslados primeiro
        const allCollapsed = Object.keys(currentState).reduce((acc, id) => {
          acc[id] = true;
          return acc;
        }, {} as Record<string, boolean>);
        
        // Retornar o estado com todos colapsados, exceto o atual
        return {
          ...allCollapsed,
          [transportationId]: false
        };
      });
    } else {
      // Se estava expandido, apenas colapsar
      setCollapsedTransportations(currentState => ({
        ...currentState,
        [transportationId]: true
      }));
    }
  };

  // Toggle para colapsar/expandir um restaurante
  const toggleRestaurantCollapse = (restaurantId: string) => {
    // If we receive an empty ID, it's just a signal to collapse all items
    if (!restaurantId) {
      setCollapsedRestaurants(currentState => {
        return Object.keys(currentState).reduce((acc, id) => {
          acc[id] = true;
          return acc;
        }, {} as Record<string, boolean>);
      });
      return;
    }
    
    const isCurrentlyCollapsed = collapsedRestaurants[restaurantId];
    
    if (isCurrentlyCollapsed) {
      // Disparar um evento para notificar outros componentes que um restaurante foi expandido
      document.dispatchEvent(new CustomEvent('item-expanded', {
        detail: {
          itemType: 'restaurant',
          itemId: restaurantId,
          pageId: page.id
        }
      }));
      
      // Update state only once, avoid setTimeout which can cause loops
      setCollapsedRestaurants(currentState => {
        // Colapsar todos os restaurantes primeiro
        const allCollapsed = Object.keys(currentState).reduce((acc, id) => {
          acc[id] = true;
          return acc;
        }, {} as Record<string, boolean>);
        
        // Retornar o estado com todos colapsados, exceto o atual
        return {
          ...allCollapsed,
          [restaurantId]: false
        };
      });
    } else {
      // Se estava expandido, apenas colapsar
      setCollapsedRestaurants(currentState => ({
        ...currentState,
        [restaurantId]: true
      }));
    }
  };

  // Toggle para colapsar/expandir um voo
  const toggleFlightCollapse = (flightId: string) => {
    // If we receive an empty ID, it's just a signal to collapse all items
    if (!flightId) {
      setCollapsedFlights(currentState => {
        return Object.keys(currentState).reduce((acc, id) => {
          acc[id] = true;
          return acc;
        }, {} as Record<string, boolean>);
      });
      return;
    }
    
    const isCurrentlyCollapsed = collapsedFlights[flightId];
    
    if (isCurrentlyCollapsed) {
      // Disparar um evento para notificar outros componentes que um voo foi expandido
      document.dispatchEvent(new CustomEvent('item-expanded', {
        detail: {
          itemType: 'flight',
          itemId: flightId,
          pageId: page.id
        }
      }));
      
      // Update state only once, avoid setTimeout which can cause loops
      setCollapsedFlights(currentState => {
        // Colapsar todos os voos primeiro
        const allCollapsed = Object.keys(currentState).reduce((acc, id) => {
          acc[id] = true;
          return acc;
        }, {} as Record<string, boolean>);
        
        // Retornar o estado com todos colapsados, exceto o atual
        return {
          ...allCollapsed,
          [flightId]: false
        };
      });
    } else {
      // Se estava expandido, apenas colapsar
      setCollapsedFlights(currentState => ({
        ...currentState,
        [flightId]: true
      }));
    }
  };

  // Funções para atualizar arrays específicos
  const updateFlights = (flights: any[]) => {
    // Create deep copies of flight objects to avoid reference issues
    // This is important for proper handling of coordinates
    const deepCopiedFlights = JSON.parse(JSON.stringify(flights));
    
    // Ensure all coordinates are properly formatted as numbers
    deepCopiedFlights.forEach((flight: any) => {
      if (flight.departureCoordinates) {
        flight.departureCoordinates = {
          lat: Number(flight.departureCoordinates.lat),
          lng: Number(flight.departureCoordinates.lng)
        };
      }
      
      if (flight.arrivalCoordinates) {
        flight.arrivalCoordinates = {
          lat: Number(flight.arrivalCoordinates.lat),
          lng: Number(flight.arrivalCoordinates.lng)
        };
      }
    });
    
    // Log for debugging
    console.log('[QuotationPageTabs] Updating flights with deep copy:', {
      flightsCount: deepCopiedFlights.length,
      withDepartureCoords: deepCopiedFlights.filter((f: any) => f.departureCoordinates).length,
      withArrivalCoords: deepCopiedFlights.filter((f: any) => f.arrivalCoordinates).length
    });
    
    updatePage(page.id, 'flights', deepCopiedFlights);
  };

  const updateAccommodations = (accommodations: any[]) => {
    updatePage(page.id, 'accommodations', accommodations);
  };

  const updateTours = (tours: any[]) => {
    updatePage(page.id, 'tours', tours);
  };
  
  const updateTransportations = (transportations: any[]) => {
    updatePage(page.id, 'transportations', transportations);
  };
  
  const updateRestaurants = (restaurants: any[]) => {
    updatePage(page.id, 'restaurants', restaurants);
  };

  // Função para encontrar o maior valor de globalOrder entre todos os itens
  const findMaxGlobalOrder = () => {
    const allItems = [
      ...(page.blocks || []),
      ...(page.flights || []),
      ...(page.accommodations || []),
      ...(page.tours || []),
      ...(page.transportations || []),
      ...(page.restaurants || [])
    ];

    if (allItems.length === 0) return -1;
    
    return Math.max(...allItems.map(item => item.globalOrder || 0));
  };

  const addFlight = () => {
    // Adicionar novo voo diretamente
    const currentDate = new Date();
    const formattedDate = format(currentDate, "yyyy-MM-dd");

    const maxOrder = findMaxGlobalOrder();
    
    const newFlight = {
      id: `flight-${Date.now()}`,
      airline: '',
      flightNumber: '',
      departureAirport: '',
      departureDateTime: `${formattedDate}T00:00:00`,
      arrivalAirport: '',
      arrivalDateTime: `${formattedDate}T02:00:00`,
      flightDuration: '',
      connections: [],
      images: [],
      globalOrder: maxOrder + 1 // Set to max + 1 to ensure it's last
    };
    
    const updatedFlights = [...(page.flights || []), newFlight];
    updateFlights(updatedFlights);
  };

  const addAccommodation = () => {
    // Adicionar nova acomodação diretamente
    const currentDate = new Date();
    const formattedDate = format(currentDate, "yyyy-MM-dd");
    
    const maxOrder = findMaxGlobalOrder();
    
    const newAccommodation = {
      id: `accommodation-${Date.now()}`,
      name: '',
      checkInDate: formattedDate,
      checkInTime: '14:00',
      checkOutDate: format(new Date(currentDate.getTime() + 86400000), "yyyy-MM-dd"), // Next day
      checkOutTime: '12:00',
      roomType: '',
      description: '',
      priceNotes: '',
      address: '',
      showAddress: true,
      images: [],
      globalOrder: maxOrder + 1 // Set to max + 1 to ensure it's last
    };
    
    const updatedAccommodations = [...(page.accommodations || []), newAccommodation];
    updateAccommodations(updatedAccommodations);
  };

  const addTour = () => {
    // Adicionar novo passeio diretamente
    const currentDate = new Date();
    const formattedDate = format(currentDate, "yyyy-MM-dd");
    
    const maxOrder = findMaxGlobalOrder();
    
    const newTour = {
      id: `tour-${Date.now()}`,
      name: '',
      date: formattedDate,
      time: '',
      duration: '',
      location: '',
      description: '',
      price: undefined,
      currency: 'BRL',
      images: [],
      globalOrder: maxOrder + 1 // Set to max + 1 to ensure it's last
    };
    
    const updatedTours = [...(page.tours || []), newTour];
    updateTours(updatedTours);
  };

  const addTransportation = () => {
    // Adicionar novo traslado diretamente
    const currentDate = new Date();
    const formattedDate = format(currentDate, "yyyy-MM-dd");
    
    const maxOrder = findMaxGlobalOrder();
    
    const newTransportation = {
      id: `transportation-${Date.now()}`,
      name: '', // Nome vazio para que o componente aplique o fallback
      date: formattedDate,
      time: '',
      duration: '',
      location: '',
      description: '',
      price: undefined,
      currency: 'BRL',
      images: [],
      globalOrder: maxOrder + 1 // Set to max + 1 to ensure it's last
    };
    
    const updatedTransportations = [...(page.transportations || []), newTransportation];
    updateTransportations(updatedTransportations);
  };

  const addRestaurant = () => {
    // Adicionar novo restaurante diretamente
    const currentDate = new Date();
    const formattedDate = format(currentDate, "yyyy-MM-dd");
    
    const maxOrder = findMaxGlobalOrder();
    
    const newRestaurant = {
      id: `restaurant-${Date.now()}`,
      name: '', // Nome vazio para que o componente RestaurantForm aplique o fallback
      date: formattedDate,
      time: '',
      duration: '',
      location: '',
      description: '',
      price: undefined,
      currency: 'BRL',
      images: [],
      globalOrder: maxOrder + 1, // Set to max + 1 to ensure it's last
      cuisineType: '',
      menuHighlights: '',
      city: '',
      country: ''
    };
    
    const updatedRestaurants = [...(page.restaurants || []), newRestaurant];
    updateRestaurants(updatedRestaurants);
  };

  // Funções para remover itens
  const removeFlight = (flightId: string) => {
    const updatedFlights = page.flights.filter(flight => flight.id !== flightId);
    updateFlights(updatedFlights);
  };

  const removeAccommodation = (accommodationId: string) => {
    const updatedAccommodations = page.accommodations ? 
      page.accommodations.filter(acc => acc.id !== accommodationId) : [];
    updateAccommodations(updatedAccommodations);
  };

  const removeTour = (tourId: string) => {
    const updatedTours = page.tours ? 
      page.tours.filter(tour => tour.id !== tourId) : [];
    updateTours(updatedTours);
  };

  const removeTransportation = (transportationId: string) => {
    const updatedTransportations = page.transportations ? 
      page.transportations.filter(transportation => transportation.id !== transportationId) : [];
    updateTransportations(updatedTransportations);
  };

  const removeRestaurant = (restaurantId: string) => {
    const updatedRestaurants = page.restaurants ? 
      page.restaurants.filter(restaurant => restaurant.id !== restaurantId) : [];
    updateRestaurants(updatedRestaurants);
  };

  // Handler para abrir o browser da biblioteca
  const handleOpenLibrary = (type: string) => {
    setCurrentItemType(type);
    setIsLibraryOpen(true);
  };
  
  // Handler para selecionar um item da biblioteca
  const handleSelectLibraryItem = (item: any) => {
    addLibraryItemToQuotation(item);
  };

  // Função para remover um tour de uma página
  const removeTourFromPage = (pageId: string, tourId: string) => {
    if (pageId === page.id) {
      const updatedTours = page.tours.filter(tour => tour.id !== tourId);
      updatePage(page.id, 'tours', updatedTours);
    }
  };
  
  // Função para adicionar um tour a uma página
  const addTourToPage = (targetPageId: string, tour: TourInfo) => {
    if (targetPageId === page.id) {
      const toursCopy = [...page.tours, { ...tour }];
      updatePage(page.id, 'tours', toursCopy);
    }
  };
  
  // Função para remover uma hospedagem de uma página
  const removeAccommodationFromPage = (pageId: string, accommodationId: string) => {
    if (pageId === page.id) {
      const updatedAccommodations = page.accommodations.filter(acc => acc.id !== accommodationId);
      updatePage(page.id, 'accommodations', updatedAccommodations);
    }
  };
  
  // Função para adicionar uma hospedagem a uma página
  const addAccommodationToPage = (targetPageId: string, accommodation: AccommodationInfo) => {
    if (targetPageId === page.id) {
      const accommodationsCopy = [...page.accommodations, { ...accommodation }];
      updatePage(page.id, 'accommodations', accommodationsCopy);
    }
  };

  // Componente para gerenciar blocos
  const blocksComponent = (
    <div className="p-4">
      <div className="flex justify-between items-center mb-2">
        <h3 className="text-base font-medium flex items-center gap-2">
          <Box className="h-5 w-5 text-[#e6a23c]" />
          Blocos
        </h3>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            size="icon" 
            onClick={() => handleOpenLibrary('block')}
            className="text-amber-600 border-amber-600 hover:bg-amber-50 h-8 w-8"
            title="Abrir Biblioteca"
          >
            <BookOpen className="h-4 w-4" />
          </Button>
          {addBlock && (
            <Button 
              type="button" 
              variant="default" 
              size="icon" 
              onClick={() => addBlock(page.id)}
              className="bg-[#087a83] hover:bg-[#076570] text-white h-8 w-8"
              title="Novo Bloco"
            >
              <Plus className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
      
      {updateBlock && deleteBlock && (
        <DragDropContext onDragEnd={(result) => {
          if (!result.destination) return;
          
          // Reordenar blocos dentro da página
          const sourceIndex = result.source.index;
          const destinationIndex = result.destination.index;
          
          // Obter a lista de blocos atual
          const blocks = Array.from(page.blocks);
          
          // Remover o item da posição original
          const [removed] = blocks.splice(sourceIndex, 1);
          
          // Inserir na nova posição
          blocks.splice(destinationIndex, 0, removed);
          
          // Atualizar os blocos da página
          updatePage(page.id, 'blocks', blocks);
        }}>
          <Droppable droppableId={`page-${page.id}-blocks-tab`} type="BLOCK">
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className={cn(
                  "space-y-4",
                  page.blocks.length === 0 && "py-4 border border-dashed rounded-md"
                )}
              >
                {page.blocks.length === 0 && (
                  <p className="text-center text-gray-500 text-sm">
                    Nenhum bloco adicionado. Clique no botão + para adicionar.
                  </p>
                )}

                {page.blocks.map((block, blockIndex) => (
                  <QuotationBlockComponent
                    key={block.id}
                    block={block}
                    index={blockIndex}
                    pageId={page.id}
                    updateBlock={updateBlock}
                    deleteBlock={deleteBlock}
                    duplicateBlock={duplicateBlock}
                    isCollapsed={collapsedBlocks[block.id] || false}
                    onToggleCollapse={() => toggleBlockCollapse(block.id)}
                    moveBlockToPage={moveBlockToPage}
                    allPages={allPages}
                  />
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </div>
  );

  // Flight Manager Component
  const flightManagerComponent = (
    <div className="p-4">
      <div className="flex justify-between items-center mb-2">
        <h3 className="text-base font-medium flex items-center gap-2">
          <Plane className="h-5 w-5 text-blue-600" />
          Voos
        </h3>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            size="icon" 
            onClick={() => handleOpenLibrary('flight')}
            className="text-amber-600 border-amber-600 hover:bg-amber-50 h-8 w-8"
            title="Abrir Biblioteca"
          >
            <BookOpen className="h-4 w-4" />
          </Button>
          <Button 
            type="button" 
            variant="default" 
            size="icon" 
            onClick={addFlight}
            className="bg-[#087a83] hover:bg-[#076570] text-white h-8 w-8"
            title="Novo Voo"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <FlightManager
        pageId={page.id}
        flights={page.flights || []}
        updateFlights={updateFlights}
        collapsedItems={collapsedFlights}
        onToggleItem={toggleFlightCollapse}
        hideAddButton={true}
        hideTitle={true}
        allPages={allPages}
        removeFlightFromPage={(pageId, flightId) => {
          if (pageId === page.id) {
            const updatedFlights = page.flights.filter(flight => flight.id !== flightId);
            updatePage(page.id, 'flights', updatedFlights);
          }
        }}
        addFlightToPage={(targetPageId, flight) => {
          if (targetPageId === page.id) {
            const flightsCopy = [...page.flights, { ...flight }];
            updatePage(page.id, 'flights', flightsCopy);
          }
        }}
        moveFlightToPage={moveFlightToPage}
      />
    </div>
  );

  // Accommodation Manager Component
  const accommodationManagerComponent = (
    <div className="p-4">
      <div className="flex justify-between items-center mb-2">
        <h3 className="text-base font-medium flex items-center gap-2">
          <Building className="h-5 w-5 text-green-600" />
          Hospedagens
        </h3>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            size="icon" 
            onClick={() => handleOpenLibrary('accommodation')}
            className="text-amber-600 border-amber-600 hover:bg-amber-50 h-8 w-8"
            title="Abrir Biblioteca"
          >
            <BookOpen className="h-4 w-4" />
          </Button>
          <Button 
            type="button" 
            variant="default" 
            size="icon" 
            onClick={addAccommodation}
            className="bg-[#087a83] hover:bg-[#076570] text-white h-8 w-8"
            title="Nova Hospedagem"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <AccommodationManager
        pageId={page.id}
        accommodations={page.accommodations || []}
        updateAccommodations={updateAccommodations}
        collapsedItems={collapsedAccommodations}
        onToggleItem={toggleAccommodationCollapse}
        hideAddButton={true}
        hideTitle={true}
        allPages={allPages}
        removeAccommodationFromPage={(pageId, accommodationId) => {
          if (pageId === page.id) {
            const updatedAccommodations = page.accommodations.filter(acc => acc.id !== accommodationId);
            updatePage(page.id, 'accommodations', updatedAccommodations);
          }
        }}
        addAccommodationToPage={(targetPageId, accommodation) => {
          if (targetPageId === page.id) {
            const accommodationsCopy = [...page.accommodations, { ...accommodation }];
            updatePage(page.id, 'accommodations', accommodationsCopy);
          }
        }}
        globalMoveAccommodationToPage={moveAccommodationToPage}
      />
    </div>
  );

  // Tour Manager Component
  const tourManagerComponent = (
    <div className="p-4">
      <div className="flex justify-between items-center mb-2">
        <h3 className="text-base font-medium flex items-center gap-2">
          <Map className="h-5 w-5 text-purple-600" />
          Tours
        </h3>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            size="icon" 
            onClick={() => handleOpenLibrary('tour')}
            className="text-amber-600 border-amber-600 hover:bg-amber-50 h-8 w-8"
            title="Abrir Biblioteca"
          >
            <BookOpen className="h-4 w-4" />
          </Button>
          <Button 
            type="button" 
            variant="default" 
            size="icon" 
            onClick={addTour}
            className="bg-[#087a83] hover:bg-[#076570] text-white h-8 w-8"
            title="Novo Tour"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <TourManager
        pageId={page.id}
        tours={page.tours || []}
        updateTours={updateTours}
        collapsedItems={collapsedTours}
        onToggleItem={toggleTourCollapse}
        hideAddButton={true}
        hideTitle={true}
        allPages={allPages}
        removeTourFromPage={(pageId, tourId) => {
          if (pageId === page.id) {
            const updatedTours = page.tours.filter(tour => tour.id !== tourId);
            updatePage(page.id, 'tours', updatedTours);
          }
        }}
        addTourToPage={(targetPageId, tour) => {
          if (targetPageId === page.id) {
            const toursCopy = [...page.tours, { ...tour }];
            updatePage(page.id, 'tours', toursCopy);
          }
        }}
        moveTourToPage={moveTourToPage}
      />
    </div>
  );

  // Transportation Manager Component
  const transportationManagerComponent = (
    <div className="p-4">
      <div className="flex justify-between items-center mb-2">
        <h3 className="text-base font-medium flex items-center gap-2">
          <Car className="h-5 w-5 text-orange-600" />
          Traslados
        </h3>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            size="icon" 
            onClick={() => handleOpenLibrary('transportation')}
            className="text-amber-600 border-amber-600 hover:bg-amber-50 h-8 w-8"
            title="Abrir Biblioteca"
          >
            <BookOpen className="h-4 w-4" />
          </Button>
          <Button 
            type="button" 
            variant="default" 
            size="icon" 
            onClick={addTransportation}
            className="bg-[#087a83] hover:bg-[#076570] text-white h-8 w-8"
            title="Novo Traslado"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <TransportationManager
        pageId={page.id}
        transportations={page.transportations || []}
        updateTransportations={updateTransportations}
        collapsedItems={collapsedTransportations}
        onToggleItem={toggleTransportationCollapse}
        hideAddButton={true}
        hideTitle={true}
        allPages={allPages}
        removeTransportationFromPage={(pageId, transportationId) => {
          if (pageId === page.id) {
            const updatedTransportations = page.transportations.filter(t => t.id !== transportationId);
            updatePage(page.id, 'transportations', updatedTransportations);
          }
        }}
        addTransportationToPage={(targetPageId, transportation) => {
          if (targetPageId === page.id) {
            const transportationsCopy = [...page.transportations, { ...transportation }];
            updatePage(page.id, 'transportations', transportationsCopy);
          }
        }}
        moveTransportationToPage={moveTransportationToPage}
      />
    </div>
  );

  // Restaurant Manager Component
  const restaurantManagerComponent = (
    <div className="p-4">
      <div className="flex justify-between items-center mb-2">
        <h3 className="text-base font-medium flex items-center gap-2">
          <Utensils className="h-5 w-5 text-red-600" />
          Restaurantes
        </h3>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            size="icon" 
            onClick={() => handleOpenLibrary('restaurant')}
            className="text-amber-600 border-amber-600 hover:bg-amber-50 h-8 w-8"
            title="Abrir Biblioteca"
          >
            <BookOpen className="h-4 w-4" />
          </Button>
          <Button 
            type="button" 
            variant="default" 
            size="icon" 
            onClick={addRestaurant}
            className="bg-[#087a83] hover:bg-[#076570] text-white h-8 w-8"
            title="Novo Restaurante"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <RestaurantManager
        pageId={page.id}
        restaurants={page.restaurants || []}
        updateRestaurants={updateRestaurants}
        collapsedItems={collapsedRestaurants}
        onToggleItem={toggleRestaurantCollapse}
        hideAddButton={true}
        hideTitle={true}
        allPages={allPages}
        removeRestaurantFromPage={(pageId, restaurantId) => {
          console.log('removeRestaurantFromPage called:', { pageId, restaurantId, currentPage: page.id });
          if (pageId === page.id) {
            console.log('removing restaurant - current restaurants:', page.restaurants);
            const updatedRestaurants = page.restaurants.filter(r => r.id !== restaurantId);
            console.log('restaurants after removal:', updatedRestaurants);
            updatePage(page.id, 'restaurants', updatedRestaurants);
          } else {
            console.log('pageId mismatch, not removing restaurant', { requestedPageId: pageId, currentPageId: page.id });
          }
        }}
        addRestaurantToPage={(targetPageId, restaurant) => {
          console.log('addRestaurantToPage called:', { targetPageId, restaurant, currentPage: page.id });
          if (targetPageId === page.id) {
            console.log('adding restaurant - current restaurants:', page.restaurants);
            const restaurantsCopy = [...page.restaurants, { ...restaurant }];
            console.log('restaurants after addition:', restaurantsCopy);
            updatePage(page.id, 'restaurants', restaurantsCopy);
          } else {
            console.log('pageId mismatch, not adding restaurant', { requestedPageId: targetPageId, currentPageId: page.id });
          }
        }}
        globalMoveRestaurantToPage={moveRestaurantToPage}
      />
    </div>
  );

  // Blocsk Component with refs
  const blocksComponentWithRef = React.useRef(blocksComponent);
  const flightManagerComponentWithRef = React.useRef(flightManagerComponent);
  const accommodationManagerComponentWithRef = React.useRef(accommodationManagerComponent);
  const tourManagerComponentWithRef = React.useRef(tourManagerComponent);
  const transportationManagerComponentWithRef = React.useRef(transportationManagerComponent);
  const restaurantManagerComponentWithRef = React.useRef(restaurantManagerComponent);

  // Debug log for moveAccommodationToPage
  useEffect(() => {
    console.log('[QuotationPageTabs] moveAccommodationToPage:', {
      exists: Boolean(moveAccommodationToPage),
      type: typeof moveAccommodationToPage,
      isFunction: typeof moveAccommodationToPage === 'function'
    });
    
    // Additional debug log that was previously in the JSX
    console.log('[QuotationPageTabs] Passando props para TabView:', {
      hasAllPages: Boolean(allPages),
      hasMoveFlightEventToPage: Boolean(moveFlightEventToPage),
      hasMoveAccommodationEventToPage: Boolean(moveAccommodationEventToPage),
      moveFlightEventToPageType: typeof moveFlightEventToPage,
      moveAccommodationEventToPageType: typeof moveAccommodationEventToPage,
      moveFlightEventToPageSource: moveFlightEventToPage ? moveFlightEventToPage.toString().substring(0, 100) + '...' : 'undefined',
      moveAccommodationEventToPageSource: moveAccommodationEventToPage ? moveAccommodationEventToPage.toString().substring(0, 100) + '...' : 'undefined'
    });
  }, [moveAccommodationToPage, allPages, moveFlightEventToPage, moveAccommodationEventToPage]);

  return (
    <>
      <TabView
        page={page}
        updatePage={updatePage}
        addFlight={addFlight}
        removeFlight={removeFlight}
        addAccommodation={addAccommodation}
        removeAccommodation={removeAccommodation}
        addTour={addTour}
        removeTour={removeTour}
        addTransportation={addTransportation}
        removeTransportation={removeTransportation}
        addRestaurant={addRestaurant}
        removeRestaurant={removeRestaurant}
        FlightManagerComponent={flightManagerComponent}
        AccommodationManagerComponent={accommodationManagerComponent}
        TourManagerComponent={tourManagerComponent}
        TransportationManagerComponent={transportationManagerComponent}
        RestaurantManagerComponent={restaurantManagerComponent}
        BlocksComponent={blocksComponent}
        addBlock={() => addBlock(page.id)}
        updateBlock={updateBlock}
        deleteBlock={deleteBlock}
        hideTitles={true}
        moveTourToPage={moveTourToPage}
        moveRestaurantToPage={moveRestaurantToPage}
        moveAccommodationToPage={moveAccommodationToPage}
        moveFlightEventToPage={moveFlightEventToPage}
        moveAccommodationEventToPage={moveAccommodationEventToPage}
        moveTransportationToPage={moveTransportationToPage}
        moveFlightToPage={moveFlightToPage}
        moveBlockToPage={moveBlockToPage}
        allPages={allPages}
      />
      
      {/* Browser da Biblioteca */}
      {isLibraryOpen && (
        <LibraryBrowser
          isOpen={isLibraryOpen}
          onClose={() => setIsLibraryOpen(false)}
          onSelectItem={handleSelectLibraryItem}
          itemType={currentItemType}
        />
      )}
    </>
  );
} 